import { formatPercent, formatValue, NARROW_QUERY, useMediaQuery, type FormatOptions } from '@/design'
import { ChartDataTable, ChartYGrid, useChartCursor } from '@/design/chartParts'
import { niceMax } from '@/design/TrendChart'

import { ChartReadout } from '../ChartReadout'

/** A series of the stack: its key, its legend label and its categorical slot (1–4, fixed, never cycled). */
export interface StackSeries {
  key: string
  label: string
  slot: 1 | 2 | 3 | 4
  /** Read out and tabled, never drawn (a negative part, refunds): its legend key is an outline, not a colour. */
  listedOnly?: boolean
}

export interface StackColumn {
  key: string
  /** The x-axis label ("3 Sep"); `title` is the readout's ("3 – 9 Sep"). */
  label: string
  title: string
  /** Each series' figure, by key: major units, or a fraction in `share` mode. Null: none for this column. */
  values: Readonly<Record<string, number | null>>
  /** Why the column has nothing to stack (no revenue that week), in place of its figures. */
  note?: string
}

/** The viewBox width of a stack in a narrow panel, or on a phone. */
export const NARROW_WIDTH = 360
const PAD = { left: 56, right: 8, top: 10, bottom: 28 }
const GAP = 2
const TICKS = 4

/**
 * Columns stacked from the baseline in the series' fixed order — revenue by week (`amount`)
 * or each part's share of the week (`share`) — with a legend always, a 2px surface gap
 * between segments, and a readout per column (pointer or arrow keys) listing every series
 * in text. A negative figure (a refund under net) is not drawn; the readout and the hidden
 * table still give it. The axis reaches the tallest stack, so nothing is clipped: under
 * net a week's shares can add up to more than 100%, refunds being a negative part of it.
 */
export function StackedColumns({
  title,
  series,
  columns,
  mode,
  money,
  width = 640,
  height = 200,
  periodHeader = 'Week',
}: {
  title: string
  series: readonly StackSeries[]
  columns: readonly StackColumn[]
  mode: 'amount' | 'share'
  money: FormatOptions
  /** The viewBox's width: a narrow panel draws a narrower chart, so its text stays legible. */
  width?: number
  height?: number
  /** The hidden table's first column header: what a column is. */
  periodHeader?: string
}) {
  // On a phone every stack is as narrow as the narrowest panel's, so its text stays legible.
  const viewWidth = useMediaQuery(NARROW_QUERY) ? Math.min(width, NARROW_WIDTH) : width
  const innerWidth = viewWidth - PAD.left - PAD.right
  const innerHeight = height - PAD.top - PAD.bottom
  const step = innerWidth / Math.max(1, columns.length)
  const cursor = useChartCursor(columns.length, viewWidth, (position) => Math.floor((position - PAD.left) / step))
  const { active, readoutId } = cursor

  const drawn = (value: number | null | undefined) => Math.max(0, value ?? 0)
  const stacked = series.filter((item) => !item.listedOnly)
  const stackTotal = (column: StackColumn) => stacked.reduce((sum, item) => sum + drawn(column.values[item.key]), 0)
  const tallest = Math.max(0, ...columns.map(stackTotal))
  const max = mode === 'share' ? Math.max(1, tallest) : niceMax(tallest)
  const y = (value: number) => PAD.top + innerHeight - (value / max) * innerHeight
  const show = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : mode === 'share' ? formatPercent(value) : formatValue(value, 'currency', money)
  const barWidth = Math.max(4, Math.min(48, step * 0.62))
  const activeColumn = active === null ? null : columns[active]
  const labelEvery = Math.max(1, Math.ceil(columns.length / 7))

  return (
    <figure className="ci-trend rv-stack">
      <div className="ci-trend__plot" aria-label={`${title}. Use the arrow keys to read each column.`} {...cursor.plotProps}>
        <svg viewBox={`0 0 ${viewWidth} ${height}`} className="ci-trend__svg" aria-hidden="true" focusable="false" {...cursor.svgProps}>
          <ChartYGrid
            max={max}
            ticks={TICKS}
            left={PAD.left}
            right={viewWidth - PAD.right}
            y={y}
            label={(value) => (mode === 'share' ? formatPercent(value, 0) : formatValue(value, 'currency', { ...money, compact: true }))}
          />
          {columns.map((column, index) => {
            const left = PAD.left + index * step + (step - barWidth) / 2
            let base = 0
            return (
              <g key={column.key} data-testid="stack-column" data-active={active === index || undefined}>
                {stacked.map((item) => {
                  const value = drawn(column.values[item.key])
                  if (value <= 0) return null
                  const top = y(base + value)
                  const bottom = y(base)
                  base += value
                  const segmentHeight = Math.max(0, bottom - top - GAP)
                  return segmentHeight > 0 ? (
                    <rect key={item.key} className="rv-stack__segment" data-slot={item.slot} x={left} y={top} width={barWidth} height={segmentHeight} rx={2} />
                  ) : null
                })}
                {index % labelEvery === 0 || index === columns.length - 1 ? (
                  <text className="ci-trend__x" x={left + barWidth / 2} y={height - 8} textAnchor="middle">
                    {column.label}
                  </text>
                ) : null}
              </g>
            )
          })}
        </svg>
        <ChartReadout id={readoutId} left={active === null ? null : ((PAD.left + (active + 0.5) * step) / viewWidth) * 100}>
          {activeColumn ? (
            <>
              <strong>{activeColumn.title}</strong>
              {activeColumn.note ? <span>{activeColumn.note}</span> : null}
              {series.map((item) => (
                <span key={item.key}>
                  {item.label}: {show(activeColumn.values[item.key])}
                </span>
              ))}
            </>
          ) : null}
        </ChartReadout>
      </div>
      <figcaption className="rv-legend">
        {series.map((item) => (
          <span key={item.key} className="rv-legend__item" data-series={item.key}>
            <span className="rv-legend__swatch" data-slot={item.listedOnly ? undefined : item.slot} data-listed-only={item.listedOnly || undefined} aria-hidden="true" />
            {item.label}
          </span>
        ))}
      </figcaption>
      <ChartDataTable
        title={title}
        columns={[periodHeader, ...series.map((item) => item.label)]}
        rows={columns.map((column) => ({
          key: column.key,
          name: column.title,
          cells: series.map((item) => (column.note ? column.note : show(column.values[item.key]))),
        }))}
      />
    </figure>
  )
}
